/**
 * Normalise errors coming out of axios + FastAPI into a friendly string.
 *
 * FastAPI's `RequestValidationError` returns:
 *   { detail: [{ loc: ["body", "email"], msg: "field required", ... }] }
 *
 * Most other errors return:
 *   { detail: "string" } or { message: "string" }
 *
 * `parseApiError(err, fallback)` returns the first human-readable string
 * it can find, or `fallback` if nothing useful comes through.
 */

const FIELD_LABEL_OVERRIDES = {
    email: "Email",
    password: "Password",
    childNo: "Registration ID",
    registrationNumber: "Registration ID",
    registration_number: "Registration ID",
    mothersNo: "Mother's number",
    mothers_no: "Mother's number",
    contactNumber: "Contact number",
    contact_number: "Contact number",
    dob: "Date of birth",
    birthDate: "Date of birth",
    birth_date: "Date of birth",
};

function prettifyField(loc) {
    if (!Array.isArray(loc)) return null;
    const field = loc.filter((p) => p !== "body" && p !== "query" && p !== "path").pop();
    if (!field) return null;
    if (FIELD_LABEL_OVERRIDES[field]) return FIELD_LABEL_OVERRIDES[field];
    return field
        .replace(/[_-]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function fromFastApiDetail(detail) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
        const parts = detail.map((d) => {
            const field = prettifyField(d?.loc);
            const msg = d?.msg || "is invalid";
            return field ? `${field}: ${msg}` : msg;
        });
        return parts.join("; ");
    }
    if (detail && typeof detail === "object") {
        if (typeof detail.msg === "string") return detail.msg;
        try {
            return JSON.stringify(detail);
        } catch {
            return "Server returned an invalid response.";
        }
    }
    return null;
}

export function parseApiError(err, fallback = "Something went wrong.") {
    if (!err) return fallback;
    if (typeof err === "string") return err;
    const data = err?.response?.data;
    if (data) {
        const fromDetail = fromFastApiDetail(data.detail) || fromFastApiDetail(data.message);
        if (fromDetail) return fromDetail;
    }
    if (err?.message) return err.message;
    return fallback;
}

export function getApiStatus(err) {
    return err?.response?.status || null;
}

export function isApiError(err, status) {
    return getApiStatus(err) === status;
}

export function isUnauthorized(err) {
    return isApiError(err, 401);
}

export function isNotFound(err) {
    return isApiError(err, 404);
}
